import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Avatar from '../components/Avatar'
import { messagesApi } from '../lib/api'
import { assetUrl } from '../utils/assetUrl'

interface Conversation {
  rentalId: string
  rentalStatus: string
  startDate: string | null
  endDate: string | null
  itemTitle: string
  itemPhoto: string | null
  otherParty: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
  lastMessage: {
    content: string
    createdAt: string
    isMine: boolean
  }
  unreadCount: number
}

// Matches MyRentals.tsx's status vocabulary/colors so a rental's state reads the
// same way everywhere in the app.
const statusLabels: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
  RETURNED: 'Return reported',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
}

const statusClasses: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  RETURNED: 'bg-slate-100 text-slate-800',
  COMPLETED: 'bg-slate-100 text-slate-800',
  CANCELLED: 'bg-red-100 text-red-800',
  DISPUTED: 'bg-red-100 text-red-800',
}

const formatDateRange = (startDate: string | null, endDate: string | null) => {
  if (!startDate || !endDate) return null
  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${fmt(startDate)} – ${fmt(endDate)}`
}

const formatTime = (iso: string) => {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function Messages() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchConversations = async () => {
    setError(null)
    try {
      const res = await messagesApi.getConversations()
      if (res.data.success) {
        setConversations(res.data.data || [])
      } else {
        setError(res.data.error || 'Could not load your messages.')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load your messages. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchConversations()
  }, [])

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6 max-w-3xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">Messages</h1>
            <p className="text-[var(--text-secondary)] mt-1">Conversations with renters and owners across your rentals.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm">{error}</p>
              <button onClick={fetchConversations} className="btn btn-primary shrink-0">
                Try again
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center text-[var(--text-secondary)]">
              Loading...
            </div>
          ) : conversations.length === 0 && !error ? (
            <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--bg-tint)] flex items-center justify-center mx-auto mb-4">
                <span className="mdi mdi-message-text-outline text-2xl text-[var(--forest-green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No conversations yet</h3>
              <p className="text-[var(--text-secondary)] max-w-md mx-auto">
                Messages you send or receive about a rental will show up here.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden">
              {conversations.map((c) => (
                <Link
                  key={c.rentalId}
                  to={`/rental/${c.rentalId}#messages`}
                  className="flex items-center gap-3 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-tint)] transition-colors"
                >
                  <Avatar
                    firstName={c.otherParty.firstName}
                    lastName={c.otherParty.lastName}
                    avatar={c.otherParty.avatar}
                    size={44}
                    className="flex-shrink-0"
                  />

                  {c.itemPhoto && (
                    <img
                      src={assetUrl(c.itemPhoto)}
                      alt={c.itemTitle}
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0 hidden sm:block"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[var(--text-primary)] truncate min-w-0">
                        {c.otherParty.firstName} {c.otherParty.lastName}
                      </span>
                      <span className="text-xs text-[var(--text-muted)] flex-shrink-0">
                        {formatTime(c.lastMessage.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] truncate min-w-0">{c.itemTitle}</p>
                    {/* Date range on its own line, not sharing space with the (possibly long,
                        truncating) item title -- this is the actual disambiguator when the same
                        renter/owner pair has rented the same item more than once, so it must
                        never be the part that gets silently cut off by truncation. Status badge
                        sits below it, right-aligned. */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      {formatDateRange(c.startDate, c.endDate) && (
                        <p className="text-xs text-[var(--text-muted)]">{formatDateRange(c.startDate, c.endDate)}</p>
                      )}
                      <span
                        className={`flex-shrink-0 ml-auto text-[0.65rem] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${statusClasses[c.rentalStatus] || 'bg-slate-100 text-slate-800'}`}
                      >
                        {statusLabels[c.rentalStatus] || c.rentalStatus}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate min-w-0 ${
                        c.unreadCount > 0 ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                      }`}
                    >
                      {c.lastMessage.isMine ? 'You: ' : ''}
                      {c.lastMessage.content}
                    </p>
                  </div>

                  {c.unreadCount > 0 && (
                    <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--forest-green)] text-white text-xs font-bold flex items-center justify-center">
                      {c.unreadCount > 9 ? '9+' : c.unreadCount}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
